import { computeCP, fullName } from "../../../../services/utilities";
import { formatScheduleLabel } from "../../../../services/redux/slices/diagnostics/clinic/appointments";

export const buildAppointmentOptions = (collections = []) =>
  collections.map((item) => ({
    value: item?._id,
    label: `${fullName(item?.patient?.fullName)} | ${item?.clinicTitle || "Clinic"} | ${getPatientHealthCardSummary(item?.patient)} | QN ${item?.qn || "--"}`,
  }));

export const getHealthCardProviderName = (card = {}) =>
  card?.provider === "phi"
    ? "PhilHealth"
    : card?.provider
      ? String(card.provider).toUpperCase()
      : "Health Card";

export const getPatientHealthCards = (patient = {}) =>
  (Array.isArray(patient?.healthCard) ? patient.healthCard : []).filter(
    (card) => card?.provider || card?.number,
  );

export const getHealthCardLabel = (card = {}) => {
  const provider = getHealthCardProviderName(card);
  const number = card?.number ? `#${card.number}` : "";

  return [provider, number].filter(Boolean).join(" ");
};

export const getHealthCardKey = (card = {}, index = 0) =>
  `${card?.provider || "card"}-${card?.number || index}-${index}`;

export const getPatientHealthCardSummary = (patient = {}) => {
  const cards = getPatientHealthCards(patient);
  if (!cards.length) return "No Health Card";

  return getHealthCardLabel(cards[0]);
};

export const getHealthCardCoverage = ({
  cards = [],
  contributions = {},
  net = 0,
}) => {
  const rawCoverage = (Array.isArray(cards) ? cards : []).reduce(
    (sum, card, index) =>
      sum + Number(contributions?.[getHealthCardKey(card, index)] || 0),
    0,
  );

  return Math.min(rawCoverage, Number(net || 0));
};

export const getSelectedClinic = (selectedAppointment = {}) =>
  selectedAppointment?.clinic || {};

export const getSelectedPhysicianName = (selectedAppointment = {}) =>
  fullName(
    selectedAppointment?.physician?.fullName ||
      selectedAppointment?.physician?.user?.fullName,
  );

export const getAppointmentMeta = (selectedAppointment = {}) => ({
  physicianName: getSelectedPhysicianName(selectedAppointment),
  scheduleLabel: selectedAppointment?.sched
    ? formatScheduleLabel(selectedAppointment.sched)
    : "--",
  queueNumber: selectedAppointment?.qn || "--",
  healthCardSummary: getPatientHealthCardSummary(selectedAppointment?.patient),
});

export const buildSettlementItems = (cart = [], privilege = 0) =>
  cart.map((item) => {
    const { discount, up, net } = computeCP(item, privilege);

    return {
      menu: item._id,
      srp: up,
      discount,
      amount: net,
      qty: item.qty,
      dispenses: Array.isArray(item?.dispenses) ? item.dispenses : [],
    };
  });

export const getCartSummary = (cart = [], privilege = 0) =>
  computeCP(cart, privilege);

export const getMedicineDosage = (menu = {}) => {
  if (menu?.dosage) return menu.dosage;
  if (String(menu?.itemType || "").toLowerCase() !== "meds") return "";
  if (!menu?.strength) return menu?.unit || "";

  return `${menu.strength} ${menu.measure || ""}/${menu.unit || "pcs"}`
    .replace(/\s+/g, " ")
    .trim();
};

export const getInventoryAvailable = (inventory = {}) => {
  const consumed = Array.isArray(inventory?.consumptions)
    ? inventory.consumptions.reduce(
        (sum, item) => sum + Number(item?.qnty || 0),
        0,
      )
    : 0;

  return Math.max(Number(inventory?.qnty || 0) - consumed, 0);
};

export const buildDefaultDispenses = ({
  qty = 0,
  inventories = [],
  clinicId = "",
  menuId = "",
}) => {
  const normalizedClinicId = String(clinicId || "");
  const normalizedMenuId = String(menuId || "");
  let remaining = Number(qty || 0);

  return (Array.isArray(inventories) ? inventories : [])
    .filter((inventory) => {
      const inventoryClinicId = String(
        inventory?.clinic?._id || inventory?.clinic || "",
      );
      const inventoryMenuId = String(
        inventory?.menu?._id || inventory?.menu || "",
      );

      return (
        (!normalizedClinicId || inventoryClinicId === normalizedClinicId) &&
        (!normalizedMenuId || inventoryMenuId === normalizedMenuId)
      );
    })
    .sort((left, right) => {
      const leftExpiry = new Date(left?.expiry || 0).getTime();
      const rightExpiry = new Date(right?.expiry || 0).getTime();

      if (leftExpiry !== rightExpiry) return leftExpiry - rightExpiry;

      return (
        new Date(left?.createdAt || 0).getTime() -
        new Date(right?.createdAt || 0).getTime()
      );
    })
    .map((inventory) => {
      const available = getInventoryAvailable(inventory);
      const deduction = remaining > 0 ? Math.min(available, remaining) : 0;
      remaining -= deduction;

      return {
        inventoryId: String(inventory?._id || ""),
        qty: deduction,
      };
    })
    .filter((item) => item.inventoryId);
};

export const buildBillingStockRows = ({
  cart = [],
  inventories = [],
  clinicId = "",
}) => {
  const normalizedClinicId = String(clinicId || "");
  const rows = [];

  (Array.isArray(cart) ? cart : [])
    .filter((item) => String(item?.itemType || "").toLowerCase() === "meds")
    .forEach((item, cartIndex) => {
      const menuId = String(item?._id || item?.menu || "");
      const defaultDispenses = buildDefaultDispenses({
        qty: item?.qty,
        inventories,
        clinicId: normalizedClinicId,
        menuId,
      });
      const dispenseMap = new Map(
        (Array.isArray(item?.dispenses) && item.dispenses.length
          ? item.dispenses
          : defaultDispenses
        ).map((dispense) => [
          String(dispense?.inventoryId || ""),
          Number(dispense?.qty || 0),
        ]),
      );

      (Array.isArray(inventories) ? inventories : []).forEach((inventory) => {
        const inventoryClinicId = String(
          inventory?.clinic?._id || inventory?.clinic || "",
        );
        const menu = inventory?.menu || {};
        const inventoryMenuId = String(menu?._id || inventory?.menu || "");
        const itemType = String(menu?.itemType || "").toLowerCase();

        if (!inventoryMenuId || itemType !== "meds") return;
        if (normalizedClinicId && inventoryClinicId !== normalizedClinicId) return;
        if (inventoryMenuId !== menuId) return;

        const stock = getInventoryAvailable(inventory);
        const dispense = Number(
          dispenseMap.get(String(inventory?._id || "")) || 0,
        );

        rows.push({
          cartIndex,
          menuId,
          inventoryId: String(inventory?._id || ""),
          label: menu?.abbreviation || menu?.description || "Unnamed medicine",
          dosage: getMedicineDosage(menu),
          expiry: inventory?.expiry || "",
          batchNo: inventory?.batchNo || "",
          stock,
          dispense,
          remaining: Math.max(stock - dispense, 0),
        });
      });
    });

  return rows.sort((left, right) => {
    if (left.cartIndex !== right.cartIndex) return left.cartIndex - right.cartIndex;

    const leftExpiry = new Date(left?.expiry || 0).getTime();
    const rightExpiry = new Date(right?.expiry || 0).getTime();
    if (leftExpiry !== rightExpiry) return leftExpiry - rightExpiry;

    return left.batchNo.localeCompare(right.batchNo);
  });
};
